import React from "react";
import { AlertTriangle, ShieldAlert } from "lucide-react";

/**
 * Dog Safety alert banner.
 *
 * Renders nothing when status is "safe" (or missing) so the UI stays clean on
 * normal days. Yellow for caution, red for danger.
 *
 * Props:
 *   safety: { status, label, advice, icon, factors } from /api/weather/* response
 *   compact?: boolean — shorter version for tight headers
 */
export default function DogSafetyBanner({ safety, compact = false }) {
  if (!safety || safety.status === "safe" || !safety.status) return null;

  const danger = safety.status === "danger";
  const Icon = danger ? ShieldAlert : AlertTriangle;
  const palette = danger
    ? {
        wrap: "bg-[#fdecec] border-[#9B4A4A]/40 text-[#5a2424]",
        chip: "bg-[#9B4A4A] text-white",
        icon: "text-[#9B4A4A]",
      }
    : {
        wrap: "bg-[#fdf5e3] border-[#D19C4C]/50 text-[#5a4318]",
        chip: "bg-[#D19C4C] text-white",
        icon: "text-[#9c6f1e]",
      };

  return (
    <div
      data-testid={`dog-safety-banner-${safety.status}`}
      role="alert"
      className={`flex items-start gap-3 border rounded-2xl px-4 py-3 ${palette.wrap}`}
    >
      <Icon size={compact ? 18 : 22} className={`${palette.icon} mt-0.5 shrink-0`} strokeWidth={2.2} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${palette.chip}`}>
            <span aria-hidden="true">{safety.icon}</span>
            {danger ? "Danger" : "Caution"}
          </span>
          <span className="font-heading text-sm sm:text-base font-medium">{safety.label}</span>
        </div>
        {safety.advice && (
          <p className={`text-[13px] sm:text-sm mt-1 leading-snug ${compact ? "line-clamp-2" : ""}`}>
            {safety.advice}
          </p>
        )}
      </div>
    </div>
  );
}
